/**
 * DocumentTableRow — spec iter182 (2026-02-27)
 *
 * Ligne de tableau extraite de `pages/Documents.jsx` (Point 1a du backlog
 * technique). Objectifs :
 *   1. Faire tomber `Documents.jsx` sous les 3300 lignes (de 4181 → 3268).
 *   2. Isoler la logique visuelle de la ligne (Identification, Type, Statut,
 *      Siège/Projet, Progression, Responsable, Pro. étape, Pièces jointes,
 *      Actions) pour la faire évoluer sans re-scroller le fichier parent.
 *   3. Zéro changement visuel — l'extraction est un pur move + wrap avec
 *      passage explicite de toutes les closures/lookups/callbacks en props.
 *
 * Contrainte : toutes les fonctions utilitaires (`stepInfo`, `fmtDateShort`,
 * `activeStepsAll`, etc.) et lookups (`typesById`, `projectsById`,
 * `usersById`, `typeViewersByType`) restent définis dans le parent
 * (`Documents.jsx`) — on les passe en props pour préserver le comportement
 * de closure exact (dépendances React, mémorisation, etc.).
 */
import { memo } from "react";
import { formatActivityShort, activityPillStyle } from "@/lib/dates";
import { useAuth } from "@/context/AuthContext";
import { BadgeCheck, Clock, Flame, Info, LockIcon as LockIcon, PauseCircle, Pencil, Trash2, Eye, Paperclip, AlertTriangle, CheckCircle2, X, XCircle } from "lucide-react";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import UserAvatar from "@/components/ui/UserAvatar";
import AttachmentsThumbnailPopover from "@/components/documents/AttachmentsThumbnailPopover";
import LinkedLotHint, { linkedLotTitle } from "@/components/documents/LinkedLotHint";
import DocumentStatusBadge, { DOC_STATUS_MAP, closureTooltip } from "@/components/documents/DocumentStatusBadge";
import ActivityHovercard from "@/components/documents/ActivityHovercard";

// STATUS_COLOR : dupliqué avec Documents.jsx car utilisé pour le liseré
// gauche. Toute évolution du mapping DOIT être répercutée dans les 2
// fichiers (mieux vaudrait à terme extraire ce mapping vers un module
// partagé mais on limite les changements dans ce point 1a).
const STATUS_COLOR = {
  en_cours: "#EAB308",
  suspendu: "#F59E0B",
  a_verifier: "#3B82F6",
  cloture: "#22C55E",
  delai_depasse: "#EF4444",
  a_refaire: "#EF4444",
  annule: "#6B7280",
};

function DocumentTableRow({
  // Document courant
  d,
  // Lookups
  typesById, projectsById, usersById, typeViewersByType,
  usersLoaded = true, typeViewersLoaded = true,
  types,
  // Composants locaux à Documents.jsx passés en props (definis dans le
  // parent car ils utilisent des closures internes — DocumentDetailsPreview
  // et DocumentProgressCell)
  DocumentDetailsPreview, DocumentProgressCell, DocumentSuiviCell,
  // Contexte utilisateur/permissions
  user, isAdminLevel, canPerm, docScope = "own",
  // Helpers de formatage/logique (definis dans le parent)
  stepInfo, attachmentsCount, activeStepsAll, activeStep,
  daysLate, pickUrgentStep, typeBadgeColor,
  fmtDateShort, fmtDateFull, fmtDateTimeShort, formatRelativeShort,
  docActivityAt, sortKey,
  // Callbacks
  saveFiltersSnapshot, navigate, openEdit, setDeleteDlg,
  // Divers
  projects,
}) {
              const t = typesById[d.type_id];
              const proj = d.project_id ? projectsById[d.project_id] : null;
              // iter200 — aucune icône sans le droit (être créateur ne donne rien) ; portée : Vue perso = ses documents, Vue globale = tous.
              const inScope = docScope === "all" || d.created_by === user?.id;
              const canEdit = inScope && canPerm("documents", "edit");
              const canDelete = inScope && canPerm("documents", "delete");
              const info = stepInfo(d);
              const tb = typeBadgeColor(d.type_id, typesById);
              const nAttach = attachmentsCount(d);
              // Spec 2026-02-XX rev9 — statut effectif : bascule automatique
              // en « delai_depasse » si une étape est overdue (calculé côté
              // serveur dans ``_decorate``). Fallback vers le status brut si
              // ancien payload sans effective_status.
              const effStatus = d.effective_status || d.status;
              const isOverdue = effStatus === "delai_depasse";
              // Couleur de base du liseré gauche = couleur du statut effectif
              // du document. Spec 2026-02-XX rev12 (batch UX) — plus de tint
              // rouge sur la ligne entière : le badge de statut « Délai dépassé »
              // suffit à signaler le retard. Le liseré reste rouge quand
              // overdue pour maintenir la lecture périphérique.
              const statusColor = STATUS_COLOR[d.status] || "#4A4A4A";
              const leftBorderColor = isOverdue ? "#EF4444" : statusColor;
              return (
                <tr
                  key={d.id}
                  className="border-b border-white/5 hover:bg-white/[0.03] transition-colors cursor-pointer"
                  onClick={() => { saveFiltersSnapshot(); navigate(`/documents/${d.id}`); }}
                  data-testid={`doc-row-${d.id}`}
                  title={`Statut : ${d.cancelled ? "Clôturé · annulé" : DOC_STATUS_MAP[effStatus]?.label || d.status || "—"}`}
                  style={{
                    // rev12 — liseré gauche uniquement (2px normal, 3px overdue),
                    // sans tint de fond. Le badge de statut porte le signal
                    // fort de retard.
                    boxShadow: `inset ${isOverdue ? 3 : 2}px 0 0 0 ${leftBorderColor}`,
                  }}
                >
                  {/* --- Identification --------------------------------- */}
                  <td className="px-3 py-2 truncate">
                    {/*
                      Spec 2026-02-XX rev4 — Réf en monospace bleu au lieu du
                      pill jaune plein. Cohérent avec l'aesthetic "code-like"
                      des références (ex: DC0040/26).
                    */}
                    <span
                      className="font-bold tabular-nums"
                      style={{
                        fontFamily: '"SF Mono","Consolas","Menlo",monospace',
                        fontSize: 12, color: "#5A9EE0", letterSpacing: "0.01em",
                      }}
                      data-testid={`doc-ref-${d.id}`}
                    >
                      {d.reference}
                    </span>
                  </td>
                  {/* --- Date de création (spec iter148, Feb 2026) --- */}
                  {/* Date de création + trait vertical 1px×14px #2a2a2e + avatar
                      du créateur. Fusionne l'ancienne colonne « Créé par »
                      (avatar seul) avec un affichage explicite de la date,
                      même pattern visuel que l'indicateur « À vérifier » des
                      Tâches. */}
                  <td className="px-3 py-2" data-testid={`doc-created-cell-${d.id}`}>
                    {(() => {
                      const u = usersById[d.created_by];
                      const dt = new Date(d.created_at || Date.now());
                      const pad = (n) => String(n).padStart(2, "0");
                      const dateStr = `${pad(dt.getDate())}-${pad(dt.getMonth() + 1)}-${String(dt.getFullYear()).slice(2)}`;
                      const timeStr = `${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
                      return (
                        <div className="flex items-center justify-center gap-2">
                          <span
                            className="text-[11px] tabular-nums whitespace-nowrap"
                            style={{ color: "#C8C8CC" }}
                            title={`Créé le ${dateStr} ${timeStr}`}
                            data-testid={`doc-created-date-${d.id}`}
                          >
                            {dateStr}
                          </span>
                          <span
                            aria-hidden="true"
                            style={{
                              display: "inline-block",
                              width: 1, height: 14, background: "#2a2a2e",
                              flexShrink: 0,
                            }}
                          />
                          <UserAvatar
                            user={u || d.created_by_user || null}
                            loading={!u && !d.created_by_user && !usersLoaded}
                            size={22}
                            variant="initials" initialsStyle={{ color: "#fff", fontSize: 9, fontWeight: 700 }}
                            subtitle={`Créé le ${dateStr} ${timeStr}`}
                            testid={`doc-creator-avatar-${d.id}`}
                          />
                        </div>
                      );
                    })()}
                  </td>
                  {/* Spec iter208 (2026-02-28) — Nouvelle colonne MODIFS.
                      Même pattern visuel que « Créé le » : date · avatar.
                      `—` si le doc n'a jamais été modifié depuis sa
                      création (`last_modified_at` null). */}
                  <td className="px-3 py-2" data-testid={`doc-modif-cell-${d.id}`}>
                    {(() => {
                      if (!d.last_modified_at) {
                        return <div className="text-center text-muted-foreground text-xs">—</div>;
                      }
                      const u = usersById[d.last_modified_by];
                      const dt = new Date(d.last_modified_at);
                      const pad = (n) => String(n).padStart(2, "0");
                      const dateStr = `${pad(dt.getDate())}-${pad(dt.getMonth() + 1)}-${String(dt.getFullYear()).slice(2)}`;
                      const timeStr = `${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
                      return (
                        <div className="flex items-center justify-center gap-2">
                          <span
                            className="text-[11px] tabular-nums whitespace-nowrap"
                            style={{ color: "#C8C8CC" }}
                            title={`Modifié le ${dateStr} ${timeStr}`}
                            data-testid={`doc-modif-date-${d.id}`}
                          >
                            {dateStr}
                          </span>
                          <span
                            aria-hidden="true"
                            style={{
                              display: "inline-block",
                              width: 1, height: 14, background: "#2a2a2e",
                              flexShrink: 0,
                            }}
                          />
                          <UserAvatar
                            user={u || d.last_modified_by_user || null}
                            loading={!u && !d.last_modified_by_user && !usersLoaded}
                            size={22}
                            variant="initials" initialsStyle={{ color: "#fff", fontSize: 9, fontWeight: 700 }}
                            subtitle={`Modifié le ${dateStr} ${timeStr}`}
                          />
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-2 text-xs font-medium truncate"
                    title={d.title || ""} data-testid={`doc-title-${d.id}`}>
                    {d.title || <span className="text-muted-foreground italic">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    {/*
                      Spec 2026-02-XX rev4 — Type = pill discrète.
                      Spec 2026-02-XX rev20 (Point O) — padding resserré.
                      Spec 2026-02-XX rev21 (Point P) — font 9px < 1300px.
                      Spec 2026-02-XX rev27 (nouveau) — le badge s'étire
                      maintenant sur 100% de la largeur de sa cellule
                      (`display:block` + `w-full`). Contenu = nom COMPLET
                      (t.name), pas le short_name — ainsi tout l'espace
                      disponible dans la cellule est utilisé, et seul le
                      dépassement effectif est tronqué par CSS
                      (`overflow:hidden` + `text-overflow:ellipsis`) avec
                      tooltip natif restituant le nom entier.
                    */}
                    <span className="block w-full text-[10px] max-[1299px]:text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded-full text-center whitespace-normal"
                      style={{
                        background: `${tb.fg}1F`,
                        border: `1px solid ${tb.fg}47`,
                        color: tb.fg,
                        letterSpacing: 0,
                        // Spec iter164 (2026-02-25) — retrait total de la
                        // troncature : le badge s'affiche EN ENTIER
                        // (whitespace-normal + wordBreak). La colonne Type
                        // a été élargie à 25% pour absorber les noms
                        // longs comme « Demande d'établissement
                        // d'attestation… » sur une seule ligne dans la
                        // très grande majorité des cas. En cas de
                        // dépassement extrême, le badge passe sur 2
                        // lignes plutôt que d'être coupé par …
                        wordBreak: "break-word",
                        lineHeight: 1.25,
                      }}
                      title={t?.name || ""}
                      data-testid={`doc-type-badge-${d.id}`}>
                      {t?.name || "—"}
                    </span>
                  </td>
                  <td className="px-3 py-2 overflow-hidden" style={{ minWidth: 0 }}>
                    {(() => {
                      // Spec iter265 point 2 (2026-02) — Quand le document est
                      // clôturé, afficher un séparateur fin + avatar de
                      // l'utilisateur qui l'a clôturé après le badge de
                      // statut. `d.closed_by` est la source canonique
                      // (finalized_by est un alias historique).
                      // iter383 pt 1 — un document annulé est aussi clôturé : même modèle (cadenas + initiales) + ✕ rouge.
                      const closed = effStatus === "cloture" || !!d.cancelled;
                      const closer = closed
                        ? (usersById[d.closed_by] || usersById[d.finalized_by] || (d.cancelled ? usersById[d.cancelled_by] : null))
                        : null;
                      const actor = d.cancelled ? (usersById[d.cancelled_by] || closer) : closer;
                      const actorName = actor ? `${actor.first_name || ""} ${actor.last_name || ""}`.trim() || actor.email : "";
                      return (
                        <div className="flex items-center gap-2 min-w-0">
                          <DocumentStatusBadge
                            status={effStatus}
                            cancelled={!!d.cancelled}
                            actorName={actorName}
                            at={d.cancelled ? d.cancelled_at : (d.closed_at || d.finalized_at)}
                            ring="#1E1E1E"
                            overdue_business_seconds={d.overdue_business_seconds}
                          />
                          {closer && (
                            <>
                              <span
                                aria-hidden="true"
                                style={{
                                  display: "inline-block",
                                  width: 1, height: 14, background: "#2a2a2e",
                                  flexShrink: 0,
                                }}
                              />
                              <UserAvatar
                                user={closer}
                                size={22}
                                variant="initials"
                                subtitle={closureTooltip({ cancelled: !!d.cancelled, actorName, at: d.cancelled ? d.cancelled_at : (d.closed_at || d.finalized_at) })}
                                data-testid={`doc-closer-avatar-${d.id}`}
                              />
                            </>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  {/* --- Destination ---------------------------------- */}
                  <td className="px-3 py-2 text-xs text-muted-foreground text-center truncate" title={proj?.name || ""} data-testid={`doc-row-project-${d.id}`}>
                    {proj?.code_label || proj?.name || "—"}
                  </td>
                  {/* --- Progression (1 col : dots réels + N/M + tooltip) --- */}
                  {/*
                    Spec 2026-02-XX rev4 — un point par étape réelle du
                    workflow ; couleur = statusColor pour les étapes closes,
                    gris pour les restantes. Format "N/M" à côté. Tooltip
                    résumant chaque étape (fermée/ouverte).
                  */}
                  <td className="px-3 py-2">
                    <DocumentProgressCell doc={d} info={info} statusColor={statusColor}
                      testId={`doc-progress-${d.id}`} />
                  </td>
                  {/* Spec iter164c (2026-02-25) — Colonnes
                      « Responsable » et « Pro. étape » RESTAURÉES à
                      la demande utilisateur. Seule la colonne
                      « Modifications » (icône crayon jaune ✎) reste
                      retirée — son contenu est accessible via l'onglet
                      Historique de la fiche détail du document. */}
                  {/* --- Personnes : Responsable uniquement (Créé par
                      est maintenant fusionné dans la colonne « Date de
                      création » ; spec iter148, Feb 2026). --- */}
                  {/* Spec 2026-02-XX rev25 (Point 6) + rev26 (Point 2) — Responsable = users avec droit "view" sur le type. Ghost avatar neutre si aucun.
                      Spec iter126 (Feb 2026) — text-center pour centrer les avatars dans la colonne. */}
                  <td className="px-3 py-2 text-center" data-testid={`doc-responsible-${d.id}`}>
                    {(() => {
                      const uids = (typeViewersByType?.[d.type_id] || []).filter(Boolean);
                      if (!typeViewersLoaded || (uids.length > 0 && !usersLoaded && !uids.some((id) => usersById[id]))) {
                        // Chargement : cercle neutre, jamais « ? ».
                        return <span aria-hidden="true" className="inline-block rounded-full shrink-0" style={{ width: 26, height: 26, background: "#26262c" }} data-testid={`doc-responsible-loading-${d.id}`} />;
                      }
                      if (uids.length === 0) {
                        // Aucun responsable affecté : « — » (distinct de « non chargé »).
                        return <span style={{ fontSize: 12, color: "#8b8b93" }} title="Aucun responsable défini pour ce type" data-testid={`doc-responsible-none-${d.id}`}>—</span>;
                      }
                      return (
                        <div className="inline-flex items-center" style={{ marginLeft: 0 }}>
                          {uids.slice(0, 3).map((uid, i) => {
                            const u = usersById[uid];
                            if (!u) return null;
                            const fullName = `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email;
                            return (
                              <span
                                key={uid}
                                className="inline-flex rounded-full shrink-0"
                                style={{ border: uids.length > 1 ? "2px solid #1B1B1B" : "1px solid rgba(255,255,255,0.15)", marginLeft: i === 0 ? 0 : -9, zIndex: 10 - i, position: "relative" }}
                                title={fullName}
                              >
                                <UserAvatar user={u} size={24} showPopover={false} />
                              </span>
                            );
                          })}
                          {uids.length > 3 && (
                            <span
                              className="inline-flex items-center justify-center rounded-full text-[9px] font-bold"
                              style={{
                                width: 26, height: 26,
                                background: "rgba(255,255,255,0.08)",
                                color: "#9CA3AF",
                                border: "2px solid #1B1B1B",
                                marginLeft: -9,
                              }}
                              title={uids.slice(3).map((uid) => {
                                const u = usersById[uid];
                                return u ? `${u.first_name} ${u.last_name}`.trim() : "";
                              }).filter(Boolean).join(", ")}
                            >
                              +{uids.length - 3}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  {/* --- Pro. étape (rev86/rev95/rev96) — cellule compacte
                       avec 3 états distincts, chacun ancré via flex : date
                       à gauche, icône à droite du bord de la cellule.
                       Taille 15px / weight 500 (rev96). --- */}
                  <td
                    className="px-3 py-2 text-center"
                    style={{ fontSize: 15, fontWeight: 500 }}
                    data-testid={`doc-deadline-${d.id}`}
                  >
                    {(() => {
                      const openSteps = activeStepsAll(d);
                      const allSteps = d.steps || [];
                      const hasSteps = allSteps.length > 0;
                      const status = d.status;

                      // Spec iter206 pt.2 (2026-02-28) — Annulé : icône X
                      // + date d'annulation (gris #6B7280), aligné à
                      // droite. Contexte : le doc est final et
                      // irréversible ; on affiche l'événement d'annulation.
                      if (status === "annule" || d.cancelled) {
                        const iso = d.cancelled_at || d.updated_at;
                        return (
                          <div
                            className="inline-flex items-center gap-1.5 whitespace-nowrap tabular-nums"
                            style={{ color: "#6B7280" }}
                            data-testid={`doc-proetat-cancelled-${d.id}`}
                            title={d.cancel_reason ? `Annulé · ${d.cancel_reason}` : "Annulé"}
                          >
                            <span>{fmtDateFull(iso)}</span>
                            <XCircle
                              size={14}
                              strokeWidth={2.2}
                              className="shrink-0"
                              style={{ color: "#6B7280" }}
                            />
                          </div>
                        );
                      }

                      // Spec rev148 (2026-02-06) — Suspendu : icône pause
                      // + date de suspension (contour orange #F97316),
                      // aligné à droite comme les autres états.
                      if (status === "suspendu") {
                        const iso = d.suspended_at || d.updated_at;
                        return (
                          <div
                            className="inline-flex items-center gap-1.5 whitespace-nowrap tabular-nums"
                            style={{ color: "#F97316" }}
                            data-testid={`doc-proetat-suspended-${d.id}`}
                            title={d.suspend_reason ? `Suspendu · ${d.suspend_reason}` : "Suspendu"}
                          >
                            <span>{fmtDateFull(iso)}</span>
                            <PauseCircle
                              size={14}
                              strokeWidth={2.2}
                              className="shrink-0"
                              style={{ color: "#F97316" }}
                            />
                          </div>
                        );
                      }

                      // ============================================================
                      // Spec rev118 (2026-02) — Popover d'historique partagé.
                      // Utilisé par :
                      //   • Cas (b) : toutes étapes remplies (Terminée OU
                      //     À vérifier).
                      //   • Cas (c) : Clôturé (avec étapes) — même contenu
                      //     + ligne "Clôturé par [avatar] · date".
                      //   • Cas (d) : Sans étape → statut Terminé.
                      //   • Cas (e) : Sans étape → Clôturé + ligne "Clôturé par".
                      // ============================================================
                      const renderStepHistoryCell = ({ cellDate, IconEl, iconColor, includeCloture }) => {
                        const closer = includeCloture
                          ? (usersById[d.finalized_by] || usersById[d.closed_by])
                          : null;
                        const closerName = closer
                          ? `${closer.first_name || ""} ${closer.last_name || ""}`.trim() || closer.email
                          : null;
                        const closureIso = includeCloture ? (d.finalized_at || d.closed_at || d.terminee_at) : null;

                        // Construit la liste des lignes d'historique :
                        // - Avec étapes snapshotées : une ligne par étape (nom, date closed_at, avatar validator).
                        // - Sans étape ET workflow_mode="simple" : ligne "Document créé et complété"
                        //   (le fallback est STRICTEMENT réservé aux docs sans workflow).
                        // - Sans étape ET workflow non-simple (cas legacy) : on utilise les étapes
                        //   du TYPE comme squelette pour afficher au moins les libellés attendus
                        //   (date/validator inconnus). Évite d'afficher "Document créé et complété"
                        //   à tort pour un doc qui a en fait un workflow défini.
                        // Spec rev125 (2026-02) — fix bug popover 1/1 "À vérifier".
                        const isSimpleWorkflow = (d.workflow_mode || "") === "simple";
                        // Spec rev126 (2026-02-05) — le fallback squelette peut
                        // provenir :
                        //   1. de `typesById[type_id].workflow_steps` (type actif) ;
                        //   2. de `d.type_workflow_steps_ref` (attaché par le backend
                        //      — inclut les types supprimés/archivés).
                        // On union les deux sources : (2) prime car il gère les
                        // types soft-deleted invisibles côté frontend.
                        const typeWorkflowSteps =
                          (d.type_workflow_steps_ref && d.type_workflow_steps_ref.length > 0)
                            ? d.type_workflow_steps_ref
                            : (typesById[d.type_id]?.workflow_steps || []);
                        const historyRows = hasSteps
                          ? allSteps.map((s, i) => {
                              const validator = s.closed_by ? usersById[s.closed_by] : null;
                              return {
                                key: s.id || i,
                                label: s.label || s.snap_label || `Étape ${i + 1}`,
                                iso: s.closed_at,
                                validator,
                              };
                            })
                          : isSimpleWorkflow
                          ? [{
                              key: "single",
                              label: "Document créé et complété",
                              iso: d.terminee_at || d.updated_at || d.created_at,
                              validator: usersById[d.terminee_by] || usersById[d.created_by],
                            }]
                          : typeWorkflowSteps.length > 0
                          ? typeWorkflowSteps.map((s, i) => ({
                              key: s.id || `wf-${i}`,
                              label: s.label || `Étape ${i + 1}`,
                              iso: null,
                              validator: null,
                              skeleton: true,
                            }))
                          : [{
                              key: "empty",
                              label: "Aucun historique d'étape",
                              iso: null,
                              validator: null,
                            }];

                        return (
                          <HoverCard openDelay={150} closeDelay={80}>
                            <HoverCardTrigger asChild>
                              <div
                                className="inline-flex items-center gap-1.5 whitespace-nowrap cursor-help tabular-nums"
                                style={{ color: iconColor }}
                                data-testid={`doc-deadline-history-${d.id}`}
                              >
                                <span>{cellDate}</span>
                                {IconEl}
                              </div>
                            </HoverCardTrigger>
                            <HoverCardContent
                              side="top"
                              align="center"
                              sideOffset={6}
                              className="p-4"
                              style={{ width: 320, background: "#0F0F10", border: "1px solid #2A2A2A" }}
                              data-testid={`doc-deadline-history-popover-${d.id}`}
                            >
                              <div className="space-y-3">
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                  Historique des étapes
                                </div>
                                {/* Spec rev119 (2026-02) — Mini barre de
                                    progression segmentée : un segment par
                                    étape du workflow. Vert (#22C55E) pour
                                    les étapes closes, gris (#3A3A3A) pour
                                    les restantes. Donne une vision
                                    d'ensemble avant de lire le détail. */}
                                {hasSteps && allSteps.length > 0 && (
                                  <div
                                    className="flex items-stretch gap-[3px] h-[6px] rounded-full overflow-hidden"
                                    data-testid={`doc-deadline-history-progress-${d.id}`}
                                  >
                                    {allSteps.map((s, i) => {
                                      const closed = !!s.closed_at;
                                      return (
                                        <div
                                          key={s.id || i}
                                          className="flex-1 rounded-[2px]"
                                          style={{
                                            background: closed ? "#22C55E" : "#3A3A3A",
                                          }}
                                          title={`${s.label || `Étape ${i + 1}`} — ${closed ? "clôturée" : "à valider"}`}
                                        />
                                      );
                                    })}
                                  </div>
                                )}
                                <div className="space-y-1.5">
                                  {historyRows.map((r) => {
                                    return (
                                      <div
                                        key={r.key}
                                        className="rounded-lg px-2.5 py-2 flex items-center justify-between gap-3"
                                        style={{ background: "#1E1E1E", border: "1px solid #2A2A2A" }}
                                        data-testid={`doc-deadline-history-row-${d.id}-${r.key}${r.skeleton ? "-skeleton" : ""}`}
                                      >
                                        <div className="min-w-0 flex-1 text-[12px] truncate" style={{ color: "#F2F2F2" }}>{r.label}</div>
                                        {/* Spec rev126 (2026-02-05) — ligne « squelette » (issue du
                                            workflow du TYPE en fallback, sans date ni validateur
                                            enregistrés) : afficher « Non tracée » en italique et à
                                            opacité 55%, à la place du couple date + avatar. */}
                                        {r.skeleton ? (
                                          <span
                                            className="text-[10px] italic whitespace-nowrap shrink-0"
                                            style={{ color: "rgba(237,237,237,0.55)" }}
                                          >
                                            Non tracée
                                          </span>
                                        ) : (
                                          <>
                                            {r.iso && (
                                              <span className="text-[10px] tabular-nums whitespace-nowrap shrink-0" style={{ color: "rgba(237,237,237,0.75)" }}>
                                                {fmtDateShort(r.iso)}
                                              </span>
                                            )}
                                            {r.validator ? (
                                              <span className="inline-flex rounded-full shrink-0" title={`${r.validator.first_name || ""} ${r.validator.last_name || ""}`.trim() || r.validator.email}>
                                                <UserAvatar user={r.validator} size={14} showPopover={false} />
                                              </span>
                                            ) : (
                                              <span className="w-[14px] h-[14px] rounded-full shrink-0" style={{ background: "rgba(255,255,255,0.05)" }} />
                                            )}
                                          </>
                                        )}
                                      </div>
                                    );
                                  })}
                                  {includeCloture && (
                                    <div
                                      className="rounded-lg px-2.5 py-2 flex items-center justify-between gap-3 mt-1"
                                      style={{ background: "rgba(139,92,246,0.10)", border: "1px solid rgba(139,92,246,0.35)" }}
                                    >
                                      <div className="min-w-0 flex-1 text-[12px] truncate" style={{ color: "#C4B5FD" }}>
                                        Clôturé par
                                      </div>
                                      {closureIso && (
                                        <span className="text-[10px] tabular-nums whitespace-nowrap shrink-0" style={{ color: "rgba(196,181,253,0.85)" }}>
                                          {fmtDateShort(closureIso)}
                                        </span>
                                      )}
                                      {closer ? (
                                        <span className="inline-flex rounded-full shrink-0" title={closerName || undefined}>
                                          <UserAvatar user={closer} size={14} showPopover={false} />
                                        </span>
                                      ) : (
                                        <span className="w-[14px] h-[14px] rounded-full shrink-0" style={{ background: "rgba(255,255,255,0.05)" }} />
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </HoverCardContent>
                          </HoverCard>
                        );
                      };

                      // ============================================================
                      // Cas (c) / (e) : CLÔTURÉ — avec ou sans étapes.
                      // Icône cadenas var(--status-cloture-docs) + popover historique + ligne
                      // "Clôturé par [avatar] + date". Spec iter102 (2026-08)
                      // — passage du violet au vert var(--status-cloture-docs) pour harmoniser.
                      // ============================================================
                      if (status === "cloture") {
                        const iso = d.finalized_at || d.closed_at || d.terminee_at;
                        return renderStepHistoryCell({
                          cellDate: fmtDateFull(iso),
                          IconEl: <LockIcon size={14} strokeWidth={2.4} className="shrink-0" />,
                          iconColor: "var(--status-cloture-docs)",
                          includeCloture: true,
                        });
                      }

                      // Spec rev87 (2026-08-02) — tooltip explicatif sur le
                      // "—" quand aucune date n'est disponible.
                      const renderDash = (reason) => (
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                className="text-muted-foreground cursor-help"
                                style={{ borderBottom: "1px dotted rgba(237,237,237,0.20)", paddingBottom: 1 }}
                                data-testid={`doc-deadline-dash-${d.id}`}
                              >
                                —
                              </span>
                            </TooltipTrigger>
                            <TooltipContent
                              side="top"
                              sideOffset={6}
                              className="max-w-[240px] text-[11px] leading-snug"
                              style={{ background: "#1E1E1E", color: "#EDEDED", border: "1px solid #2A2A2A" }}
                            >
                              {reason}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );

                      // ============================================================
                      // Cas (b) / (d) : TOUTES ÉTAPES REMPLIES (Terminée OU
                      // À vérifier) — avec étapes. OU document SANS étape en
                      // statut Terminé (transition auto). Rendu unifié :
                      // BadgeCheck vert #22C55E + popover historique.
                      // Spec rev118 (2026-02) — remplace la règle rev108 qui
                      // distinguait terminee (vert) et termine (date brute).
                      // ============================================================
                      if (openSteps.length === 0) {
                        if (hasSteps && (status === "terminee" || status === "termine")) {
                          const lastIso = allSteps
                            .map((s) => s.closed_at)
                            .filter(Boolean)
                            .sort()
                            .slice(-1)[0] || d.terminee_at;
                          return renderStepHistoryCell({
                            cellDate: fmtDateShort(lastIso),
                            IconEl: <BadgeCheck size={15} strokeWidth={2.4} className="shrink-0" />,
                            iconColor: "#22C55E",
                            includeCloture: false,
                          });
                        }
                        // Sans étape + statut terminée (transition auto) → cas (d)
                        if (!hasSteps && (status === "terminee" || status === "termine")) {
                          const iso = d.terminee_at || d.updated_at || d.created_at;
                          return renderStepHistoryCell({
                            cellDate: fmtDateShort(iso),
                            IconEl: <BadgeCheck size={15} strokeWidth={2.4} className="shrink-0" />,
                            iconColor: "#22C55E",
                            includeCloture: false,
                          });
                        }
                        if (hasSteps) {
                          // Cas atypique : toutes étapes closes mais statut
                          // en_cours / suspendu / autre — date brute neutre
                          // sans icône (comportement legacy conservé).
                          const lastIso = allSteps
                            .map((s) => s.closed_at)
                            .filter(Boolean)
                            .sort()
                            .slice(-1)[0] || d.terminee_at;
                          return (
                            <div
                              className="inline-flex items-center whitespace-nowrap tabular-nums"
                              style={{ color: "#EDEDED" }}
                              title={lastIso ? `Dernière étape validée le ${new Date(lastIso).toLocaleString("fr-FR")}` : undefined}
                              data-testid={`doc-deadline-last-${d.id}`}
                            >
                              <span>{fmtDateShort(lastIso)}</span>
                            </div>
                          );
                        }
                        // Aucune étape + statut non-terminé → dash
                        return renderDash("Aucune étape de workflow — ce type n'en définit pas");
                      }

                      // ============================================================
                      // Cas (a) : EN COURS — étapes encore ouvertes. Rendu
                      // date d'échéance + icône horloge #EDEDED + popover
                      // "PROCHAINE ÉTAPE" mini-table (comportement existant).
                      // ============================================================
                      const urgent = pickUrgentStep(openSteps);
                      if (!urgent) {
                        // Étape(s) ouverte(s) mais aucune n'a de deadline
                        const many = openSteps.length > 1;
                        return renderDash(
                          many
                            ? "Aucun délai configuré pour ces étapes"
                            : "Aucun délai configuré pour cette étape"
                        );
                      }
                      const isLate = urgent.is_late === true;
                      const lateDays = isLate ? daysLate(urgent.deadline) : 0;
                      // Spec rev124 (2026-02) — format « 05 jours » (zéro
                      // devant, mot en toutes lettres) + icône flamme
                      // Tabler `ti-flame`, remplace « Retard · Xj » + Clock.
                      const cellLabel = isLate
                        ? `${String(lateDays).padStart(2, "0")} jours`
                        : fmtDateShort(urgent.deadline);
                      return (
                        <HoverCard openDelay={150} closeDelay={80}>
                          <HoverCardTrigger asChild>
                            <div
                              className="inline-flex items-center gap-1.5 cursor-help whitespace-nowrap tabular-nums"
                              style={{ color: isLate ? "#EF4444" : "#EDEDED" }}
                              data-testid={`doc-deadline-cell-${d.id}`}
                            >
                              <span>{cellLabel}</span>
                              {isLate ? (
                                <Flame
                                  size={15}
                                  strokeWidth={2.2}
                                  className="shrink-0"
                                  style={{ color: "#EF4444" }}
                                />
                              ) : (
                                <Clock
                                  size={14}
                                  strokeWidth={2.2}
                                  className="shrink-0"
                                  style={{ color: "rgba(237,237,237,0.55)" }}
                                />
                              )}
                            </div>
                          </HoverCardTrigger>
                          <HoverCardContent
                            side="top"
                            align="center"
                            sideOffset={6}
                            className="p-4"
                            style={{ width: 320, background: "#0F0F10", border: "1px solid #2A2A2A" }}
                            data-testid={`doc-deadline-popover-${d.id}`}
                          >
                            <div className="space-y-3">
                              <div className="flex items-center gap-2">
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                  Prochaine{openSteps.length > 1 ? "s" : ""} étape{openSteps.length > 1 ? "s" : ""}
                                </div>
                                {openSteps.length > 1 && (
                                  <span className="text-[10px] text-muted-foreground">
                                    · {openSteps.length} ouvertes
                                  </span>
                                )}
                              </div>
                              {/* Spec rev119 (2026-02) — Mini barre segmentée
                                  aussi dans le popover "Prochaines étapes"
                                  pour donner une vision d'ensemble : vert
                                  pour les closes, gris pour les restantes. */}
                              {allSteps.length > 0 && (
                                <div
                                  className="flex items-stretch gap-[3px] h-[6px] rounded-full overflow-hidden"
                                  data-testid={`doc-deadline-progress-${d.id}`}
                                >
                                  {allSteps.map((s, i) => (
                                    <div
                                      key={s.id || i}
                                      className="flex-1 rounded-[2px]"
                                      style={{
                                        background: s.closed_at ? "#22C55E" : "#3A3A3A",
                                      }}
                                      title={`${s.label || `Étape ${i + 1}`} — ${s.closed_at ? "clôturée" : "à valider"}`}
                                    />
                                  ))}
                                </div>
                              )}
                              <div className="space-y-1.5">
                                {openSteps.map((s, i) => {
                                  const sLate = s.is_late === true;
                                  const sDays = sLate ? daysLate(s.deadline) : 0;
                                  const label = s.label || s.snap_label || `Étape ${i + 1}`;
                                  return (
                                    <div
                                      key={s.id || i}
                                      className="rounded-lg px-2.5 py-2 flex items-center justify-between gap-3"
                                      style={{ background: "#1E1E1E", border: "1px solid #2A2A2A" }}
                                      data-testid={`doc-deadline-step-${d.id}-${i}`}
                                    >
                                      <div className="min-w-0 flex-1">
                                        <div className="text-[12px] text-white truncate">{label}</div>
                                      </div>
                                      {sLate ? (
                                        <span
                                          className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full whitespace-nowrap shrink-0"
                                          style={{ background: "rgba(239,68,68,0.14)", color: "#EF4444", border: "1px solid rgba(239,68,68,0.40)" }}
                                        >
                                          {String(sDays).padStart(2, "0")} jours
                                        </span>
                                      ) : s.deadline ? (
                                        <span
                                          className="text-[10px] tabular-nums px-2 py-0.5 rounded-full whitespace-nowrap shrink-0"
                                          style={{ background: "rgba(255,255,255,0.06)", color: "rgba(237,237,237,0.75)", border: "1px solid rgba(255,255,255,0.14)" }}
                                        >
                                          {fmtDateShort(s.deadline)}
                                        </span>
                                      ) : (
                                        <span className="text-[10px] text-muted-foreground shrink-0">Sans échéance</span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </HoverCardContent>
                        </HoverCard>
                      );
                    })()}
                  </td>

                  {/* --- Contenu (📎) — Point 10 : icône trombone légèrement + grande --- */}
                  <td className="px-3 py-2 text-xs text-center" title={(d.crm_lots_ref || []).length ? linkedLotTitle(d.crm_lots_ref) : undefined} data-testid={`doc-attach-${d.id}`}>
                    <div className="inline-flex flex-col items-center" style={{ gap: 1, lineHeight: "14px" }}>
                      <AttachmentsThumbnailPopover
                        doc={d}
                        docType={types.find((t) => t.id === d.type_id)}
                        count={nAttach}
                        iconSize={16}
                      />
                      <LinkedLotHint lots={d.crm_lots_ref || []} docId={d.id} />
                    </div>
                  </td>
                  {/* Spec iter264 point 5 (2026-02) — Cellule « Activité »
                      en pastille compacte. 3 seuils temporels :
                        • < 1 h  → vert (fond #1a2a1e / texte #5dcaa5)
                        • < 24 h → neutre (fond #1e1e20 / texte #c8c8cc)
                        • ≥ 24 h → neutre atténué (fond #1e1e20 / texte #8a8a8f)
                      Format court sans « il y a » (spec formatRelativeShort). */}
                  {sortKey === "recent_activity" && (
                    <td className="px-2 py-2 whitespace-nowrap text-center overflow-hidden" style={{ maxWidth: 0 }} data-testid={`doc-activity-${d.id}`}>
                      {(() => {
                        const iso = docActivityAt(d);
                        if (!iso) return (
                          <span className="italic text-[10.5px]" style={{ color: "rgba(255,255,255,0.25)" }}>—</span>
                        );
                        return (
                          <ActivityHovercard doc={d} user={usersById[d.activity?.actor_id]} usersById={usersById}>
                            <span className="inline-flex items-center tabular-nums" style={activityPillStyle(iso)}>
                              {formatActivityShort(iso)}
                            </span>
                          </ActivityHovercard>
                        );
                      })()}
                    </td>
                  )}
                  {/* --- Actions -------------------------------------- */}
                  <td className="px-3 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    {/* Icône Détails (hover popover) — spec 2026-07-24 feature #5,
                        palette bleue (spec 2026-02-XX rev4).
                        Spec 2026-02-XX rev28 — harmonisation avatar rond 30px
                        avec les 2 autres icônes de la colonne. */}
                    <HoverCard openDelay={150} closeDelay={80}>
                      <HoverCardTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex items-center justify-center mr-1 transition-all hover:scale-[1.08] hover:saturate-[1.15]"
                          data-testid={`doc-details-${d.id}`}
                          aria-label="Aperçu du document"
                          style={{
                            width: 30, height: 30, borderRadius: 999,
                            background: "rgba(90,158,224,0.14)",
                            border: "1px solid rgba(90,158,224,0.38)",
                            color: "#5A9EE0",
                          }}
                        >
                          <Info size={14} strokeWidth={2.4} />
                        </button>
                      </HoverCardTrigger>
                      {(() => {
                        // Spec rev99 (2026-08-02) — sur les documents dont
                        // « Pro. étape » est Terminé (auto) ou Clôturé, le
                        // popover ⓘ affiche l'historique compact des
                        // étapes (nom / date JJ/MM / avatar 14px).
                        // Sinon fallback vers l'aperçu complet historique.
                        //
                        // Spec rev107 (2026-02) — bug DC0061/26 : la
                        // condition se basait sur la progression (`toutes
                        // les étapes fermées`), ce qui incluait à tort le
                        // statut "À vérifier" (`termine`) qui a lui aussi
                        // toutes ses étapes fermées mais attend une action
                        // du manager. On restreint STRICTEMENT au statut
                        // du document — plus de fallback progression.
                        const isDone = d.status === "cloture" || d.status === "terminee";
                        if (!isDone) {
                          return (
                            <HoverCardContent
                              align="end"
                              side="left"
                              className="p-4 text-xs"
                              style={{ width: 440, background: "#0F0F10", border: "1px solid #2A2A2A" }}
                              data-testid={`doc-details-content-${d.id}`}
                            >
                              <DocumentDetailsPreview doc={d} usersById={usersById} projects={projects} type={types.find((t) => t.id === d.type_id)} />
                            </HoverCardContent>
                          );
                        }
                        // Historique compact : 1 ligne par étape + ligne
                        // synthétique « Clôturé » si status = cloture.
                        const allSteps = d.steps || [];
                        const rows = [];
                        const sorted = [...allSteps].sort((a, b) => {
                          const ao = a.order ?? 0, bo = b.order ?? 0;
                          if (ao !== bo) return ao - bo;
                          return String(a.closed_at || "").localeCompare(String(b.closed_at || ""));
                        });
                        for (const s of sorted) {
                          const u = usersById[s.closed_by];
                          rows.push({
                            key: `s-${s.id}`,
                            label: s.label || s.snap_label || "Étape",
                            iso: s.closed_at,
                            user: u,
                          });
                        }
                        if (d.status === "cloture") {
                          rows.push({
                            key: `cloture`,
                            label: "Clôturé",
                            iso: d.finalized_at || d.closed_at,
                            user: usersById[d.finalized_by] || usersById[d.closed_by],
                          });
                        }
                        const fmtDDMM = (iso) => {
                          if (!iso) return "—";
                          try {
                            const dt = new Date(iso);
                            const dd = String(dt.getDate()).padStart(2, "0");
                            const mm = String(dt.getMonth() + 1).padStart(2, "0");
                            return `${dd}/${mm}`;
                          } catch { return "—"; }
                        };
                        return (
                          <HoverCardContent
                            align="end"
                            side="left"
                            className="p-3"
                            style={{ width: 220, background: "#1E1E1E", border: "1px solid #2A2A2A", borderRadius: 6 }}
                            data-testid={`doc-history-content-${d.id}`}
                          >
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                              Historique des étapes
                            </div>
                            <div className="space-y-1.5">
                              {rows.map((r) => {
                                return (
                                  <div key={r.key} className="flex items-center justify-between gap-2 text-[11px]">
                                    <span className="truncate" style={{ color: "#F2F2F2" }} title={r.label}>{r.label}</span>
                                    <span className="inline-flex items-center gap-1.5 shrink-0">
                                      <span className="tabular-nums text-muted-foreground">{fmtDDMM(r.iso)}</span>
                                      {r.user ? (
                                        <span className="inline-flex rounded-full" aria-label={`${r.user.first_name || ""} ${r.user.last_name || ""}`.trim()}>
                                          <UserAvatar user={r.user} size={14} showPopover={false} />
                                        </span>
                                      ) : (
                                        <span className="text-muted-foreground text-[9px]">—</span>
                                      )}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </HoverCardContent>
                        );
                      })()}
                    </HoverCard>
                    {canEdit && (
                      <button
                        type="button"
                        onClick={canEdit ? () => openEdit(d) : undefined}
                        title={canEdit ? "Modifier" : "Permission requise"}
                        disabled={!canEdit}
                        className="inline-flex items-center justify-center mr-1 transition-all hover:scale-[1.08] hover:saturate-[1.15] disabled:hover:scale-100 disabled:hover:saturate-100"
                        data-testid={`doc-edit-${d.id}`}
                        style={{
                          // Spec 2026-02-XX rev28 — avatar rond 30px, fond gris
                          // sombre plein #262931, crayon gris clair. Volontairement
                          // distinct du jaune plein de la colonne Historique
                          // (fond solide vs solide, mais teinte neutre grise
                          // opposée au brand yellow — aucune confusion possible).
                          width: 30, height: 30, borderRadius: 999,
                          background: "#262931",
                          border: "1px solid rgba(156,163,175,0.28)",
                          color: "#9CA3AF",
                          opacity: canEdit ? 1 : 0.45,
                          cursor: canEdit ? "pointer" : "not-allowed",
                        }}
                      >
                        <Pencil size={14} strokeWidth={2.4} />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        type="button"
                        onClick={canDelete ? () => setDeleteDlg({ doc: d }) : undefined}
                        title={canDelete ? "Supprimer" : "Permission requise"}
                        disabled={!canDelete}
                        className="inline-flex items-center justify-center ml-1 transition-all hover:scale-[1.08] hover:saturate-[1.15] disabled:hover:scale-100 disabled:hover:saturate-100"
                        data-testid={`doc-delete-${d.id}`}
                        style={{
                          width: 30, height: 30, borderRadius: 999,
                          background: "rgba(239,68,68,0.14)",
                          border: "1px solid rgba(239,68,68,0.38)",
                          color: "#EF4444",
                          opacity: canDelete ? 1 : 0.45,
                          cursor: canDelete ? "pointer" : "not-allowed",
                        }}
                      >
                        <Trash2 size={14} strokeWidth={2.4} />
                      </button>
                    )}
                  </td>
                </tr>
              );
}

// Spec iter186 (2026-02-27) — React.memo pour éviter le re-render de
// toutes les lignes quand un seul document change de statut. La comparaison
// se fait sur `d.id + d.updated_at` : tant que ces deux champs sont
// inchangés, la ligne reste stable même si le parent re-render pour une
// autre raison (nouveau document ajouté à la liste, filtre modifié, etc.).
//
// Trade-off : si `usersById` ou `typesById` reçoit un nouvel utilisateur/
// type actif dans le rendu (rare hors chargement initial), la ligne ne
// se rafraîchira pas jusqu'au prochain `updated_at` du document. C'est
// acceptable pour l'usage (10-15 utilisateurs, ~80 documents actifs).
export default memo(DocumentTableRow, (prev, next) => {
  // Retourne `true` = props identiques → skip re-render.
  return (
    prev.d.id === next.d.id &&
    prev.d.updated_at === next.d.updated_at &&
    prev.d.effective_status === next.d.effective_status &&
    prev.sortKey === next.sortKey
  );
});
